import { Component, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { TokenStorageService } from '../../services/token-storage.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-form-loggin',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule],
  templateUrl: './form-loggin.component.html',
  styleUrls: ['./form-loggin.component.css']
})
export class FormLogginComponent {
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private tokenStorage = inject(TokenStorageService);

  email = '';
  password = '';
  isLoading = false;
  errorMessage: string | null = null;
  showResendVerification = false;

  // FontAwesome icons and UI state
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  showPassword = false;

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  async login() {
    if (!this.validateForm()) return;

    this.isLoading = true;
    this.errorMessage = null;
    this.showResendVerification = false;

    try {
      await this.authService.signIn(this.email, this.password);
      await this.handleSuccessfulLogin();
    } catch (error: any) {   
      if (error instanceof Error && error.message === 'NEW_PASSWORD_REQUIRED') {
        // La session ya se guarda en localStorage desde el AuthService
        await this.router.navigate(['/change-password'], { queryParams: { mode: 'FORCE_CHANGE', email: this.email } });
        return;
      }
      this.handleLoginError(error);
    } finally {
      this.isLoading = false;
    }
  }

  private validateForm(): boolean {
    if (!this.email || !this.password) {
      this.errorMessage = 'Por favor completa todos los campos';
      return false;
    }
    return true;
  }

  private async handleSuccessfulLogin() {
    const user = this.tokenStorage.getUser();
    const groups: string[] = user?.groups || [];
    if (groups.includes('superadmin')) {
      await this.router.navigate(['/superadmin']);
    } else if (groups.includes('admin-hotel')) {
      await this.router.navigate(['/admin-hotel']);
    } else if (groups.includes('admin-recepcion')) {
      await this.router.navigate(['/admin-recepcion']);
    } else {
      await this.router.navigate(['/']); 
    }
  }

 private handleLoginError(error: unknown) {
  if (error instanceof Error) {
    this.errorMessage = this.getUserFriendlyError(error);
    if (error.message.includes('UserNotConfirmedException')) {
      this.handleUnconfirmedUser();
    }
  } else {
    console.error('Error desconocido en login:', error);
    this.errorMessage = 'Ocurrió un error inesperado. Por favor, inténtalo de nuevo.';
  }
}

  private getUserFriendlyError(error: Error): string {
    const message = error.message;
    
    if (message.includes('InvalidParameterException')) {
      return 'Configuración inválida. Contacta al soporte técnico.';
    }
    if (message.includes('NotAuthorizedException')) {
      return 'Usuario o contraseña incorrectos';
    }
    if (message.includes('UserNotFoundException')) {
      return 'Usuario no registrado';
    }
    return message;
  }

  private handleUnconfirmedUser() {
    this.showResendVerification = true;
    this.router.navigate(['/confirm-registration'], {
      queryParams: { email: this.email }
    });
  }

  async resendVerificationCode() {
    try {
      await this.authService.resendConfirmationCode(this.email);
      alert('Código de verificación reenviado. Revisa tu correo electrónico.');
    } catch (error: unknown) {
      this.errorMessage = error instanceof Error ? error.message : 'Error al reenviar el código';
    }
  }
}